import { LocalDate, Month } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { APISpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import {
  Field,
  Form,
  Parsing,
  checkBox,
  constantField,
  selectBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled, tryExtractErrorMessage } from "trader.ts/utils";
import {
  internalteacherid,
  thirdpartysellerid,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import {
  checkboxesField,
  checkboxesFieldRenderMultiselectWithPopup,
} from "../../client/checkboxesfield";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import { invoiceSubjectFilter } from "../../domain/finance/types";
import { debounce } from "../../lib/debounce";
import { isEqual } from "../../lib/isequal";
import { isNil } from "../../lib/istype";
import {
  ddMMyyyy,
  renderCurrency,
  renderedInvoiceSubjectToHtml,
  renderFirstNameLastName,
  renderInvoiceSubjectForOverviews,
} from "../../render";
import * as routes from "../../routes";
import { renderPeriod, translateMonth } from "../../translations_specific";
import { stringToJsxClientside } from "../editemailtemplates/client";
import { match } from "ts-pattern";

export function render(opts: {
  internalTeachers: {
    firstname: string;
    lastname: string;
    internalteacherid: internalteacherid;
  }[];
  months: [number, Month][];
  hasPeriodFees: boolean;
  vatPercentages: { vat: number }[];
  thirdPartySellers: { id: thirdpartysellerid; name: string }[];
}): HTMLElement | HTMLElement[] {
  const today = LocalDate.now();
  const initialMonth =
    // This month or first future month
    opts.months
      .filter(
        (m) =>
          (isEqual(m[1], today.month()) && m[0] === today.year()) ||
          LocalDate.from(today).withDayOfMonth(1).isAfter(today),
      )
      .first() ||
    opts.months.last() ||
    null;
  if (!initialMonth) {
    throw new Error("Not months");
  }

  const initialShowAllMonths = opts.months.length > 20 ? false : true;

  const form = new Form({})
    .addField("showAllMonths", [], () =>
      initialShowAllMonths === true
        ? constantField(true, { render: [] })
        : checkBox({
            initialVal: initialShowAllMonths,
            label: "Toon meer maanden",
          }),
    )
    .addField("months", ["showAllMonths"], async ({ showAllMonths }) => {
      const orderedMonths = opts.months.orderBy(
        [(m) => m[0], (m) => m[1].ordinal()],
        ["desc", "desc"],
      );
      const initialMonthIndex = orderedMonths.indexOf(initialMonth);
      return checkboxesField({
        initial: [initialMonth],
        values: orderedMonths.take(
          showAllMonths ? 1000 : initialMonthIndex + 12,
        ),
        show: (s) => translateMonth(s[1]) + " " + s[0],
        addNoFilterOption: false,
        render: checkboxesFieldRenderMultiselectWithPopup,
        label: "Aangemaakt in",
      });
    })
    .addField("paid", [], async () =>
      selectBox({
        values: ["All" as const, "OnlyUnpaid" as const, "OnlyPaid" as const],
        initial: "All" as const,
        show: (s) =>
          s === "All"
            ? "Allemaal"
            : s === "OnlyUnpaid"
            ? "Alleen onbetaalde"
            : s === "OnlyPaid"
            ? "Alleen betaalde"
            : checkAllCasesHandled(s),
        // render: radioFieldCheckboxRender,
        label: "Betaalstatus",
      }),
    )
    .addField("thirdpartyseller", [], async () => {
      const values = [
        { tag: "no filter" as const },
        { tag: "none" as const },
        ...opts.thirdPartySellers.map((tps) => ({
          tag: "thirdpartyseller" as const,
          item: tps,
        })),
      ];
      return opts.thirdPartySellers.length === 0
        ? constantField({ tag: "no filter" as const })
        : selectBox({
            initial: { tag: "no filter" as const },
            values: values,
            show: (n) =>
              n.tag === "none"
                ? "Geen vereniging"
                : n.tag === "no filter"
                ? "Geen filter"
                : n.item.name,
            // render: radioFieldCheckboxRender,
            label: "Vereniging",
          });
    })
    .addField(
      "vat",
      [],
      async (): Promise<
        Field<{ tag: "no filter" } | { tag: "vat"; vat: number }>
      > => {
        if (opts.vatPercentages.length > 1) {
          const values = [
            { tag: "no filter" as const },
            ...opts.vatPercentages.map((perc) => ({
              tag: "vat" as const,
              vat: perc.vat,
            })),
          ];
          return selectBox({
            initial: values[0],
            values,
            label: "BTW",
            show: (v) =>
              v.tag === "no filter"
                ? "Geen filter"
                : v.tag === "vat"
                ? v.vat.toString() + "%"
                : checkAllCasesHandled(v),
          });
        } else {
          return constantField({ tag: "no filter" as const });
        }
      },
    )
    .addField("subject", [], async () => {
      return invoiceSubjectFilterField(opts);
    })
    .build({
      render: (fields) => (
        <section
          className={style.equal_columns + style.cancel_main_margins}
          style="margin-top: -32px"
        >
          <div>
            {fields.months()}
            <div style="text-align: right;">{fields.showAllMonths()}</div>
          </div>
          {fields.paid()}
          {fields.thirdpartyseller()}
          {fields.vat()}
          {fields.subject()}
        </section>
      ),
    });

  const res = runQueryOnFormChange(
    form.s,
    routes.queryInvoiceStatisticsSpec,
    (t) => t,
  );

  const id = "classy-table-id";

  return (
    <main style="max-width: unset;">
      <section
        className={style.cancel_main_margins}
        style="background-color: var(--grey-200); padding-bottom: 32px;"
      >
        {"Hier krijg je een overzicht van alle aangemaakte facturen en creditnota's, onder andere nuttig voor de boekhouding. Je krijgt elke factuur en creditnota met hun totaalbedrag en datum te zien. Je ziet hier ook het te betalen bedrag, met creditnota's reeds in verrekend."}
      </section>

      {form.render()}

      {renderReportResult(res, (rows, query) => {
        const downloadLinks = renderTableDownloadLinks({
          tableid: id,
          filename: "OverzichtFacturen",
        });
        const vatSelectedExplanation =
          query.vat.tag === "no filter" ? (
            []
          ) : (
            <section>
              {stringToJsxClientside(
                "Je selecteerde een filter op BTW. Omdat BTW betrekking heeft op de individuele factuurlijnen, tonen we <b>per factuurlijn</b> met het gevraagde BTW percentage een rij in onderstaande tabel.",
              )}
            </section>
          );
        const headerRow = (
          <tr>
            <th>{"Nummer"}</th>
            <th>{"Student"}</th>
            <th>{"Onderwerp"}</th>
            <th>{"Aangemaakt"}</th>
            <th>{"Eerste les"}</th>
            <th style="display: none">{"Schooljaar"}</th>
            <th>{"Bedrag"}</th>
            {query.vat.tag === "no filter" ? (
              <th>{"Te betalen"}</th>
            ) : (
              []
            )}
            <th>{"BTW"}</th>
            <th>{"Bedrag excl BTW"}</th>
            <th>{"Extra"}</th>
          </tr>
        );
        const renderedRows =
          rows === null
            ? [<tr></tr>]
            : rows.map((row) => {
                return (
                  <tr>
                    <td>
                      <a
                        href={routes.invoicedetailsRoute.link({
                          edit: false,
                          invoiceid: row.id,
                        })}
                      >
                        {row.externalid}
                      </a>
                    </td>
                    <td>{renderFirstNameLastName(row.subject.student)}</td>
                    <td>
                      {renderedInvoiceSubjectToHtml(
                        renderInvoiceSubjectForOverviews(row.subject),
                      )}
                    </td>
                    <td>{row.created.format(ddMMyyyy)}</td>
                    <td>
                      {match(row.subject)
                        .with({ tag: "enrollment" }, (e) => e.lg.firstlesson)
                        .with(
                          { tag: "enrollmentrequest" },
                          (e) => e.enr_req.contents.firstlesson,
                        )
                        .with({ tag: "periodfee" }, () => null)
                        .with({ tag: "student_charge" }, () => null)
                        .exhaustive()
                        ?.format(ddMMyyyy) || ""}
                    </td>
                    <td style="display: none">
                      {renderPeriod(
                        row.subject.period.firstday,
                        row.subject.period.lastday,
                      )}
                    </td>
                    <td>{renderCurrency(row.amount_total)}</td>
                    {query.vat.tag === "no filter" ? (
                      <td style={`background-color: ${row.bgcolor};`}>
                        {isNil(row.amount_left)
                          ? ""
                          : renderCurrency(row.amount_left)}
                      </td>
                    ) : (
                      []
                    )}
                    <td>
                      {row.amount_vat ? renderCurrency(row.amount_vat) : ""}
                    </td>
                    <td>
                      {row.amount_vat
                        ? renderCurrency(
                            row.amount_total.subtract(row.amount_vat),
                          )
                        : renderCurrency(row.amount_total)}
                    </td>
                    <td>
                      {row.extra ? (
                        row.extra.link ? (
                          <a href={row.extra.link}>{row.extra.text}</a>
                        ) : (
                          <span>{row.extra.text}</span>
                        )
                      ) : (
                        ""
                      )}
                    </td>
                  </tr>
                );
              });
        const renderedTotal =
          rows === null ? (
            <span></span>
          ) : (
            <tr style="font-weight: bold;background-color: var(--grey-100);">
              <td>{"Totaal" + ": " + rows.length}</td>
              <td></td>
              <td></td>
              <td style="display: none"></td>
              <td></td>
              <td></td>
              <td>
                {renderCurrency(
                  rows.reduce(
                    (acc, row) => acc.add(row.amount_total),
                    currency(0),
                  ),
                )}
              </td>
              {query.vat.tag === "no filter" ? (
                <td>
                  {renderCurrency(
                    rows.reduce(
                      (acc, row) => acc.add(row.amount_left || currency(0)),
                      currency(0),
                    ),
                  )}
                </td>
              ) : (
                []
              )}
              <td>
                {renderCurrency(
                  rows.reduce(
                    (acc, row) => acc.add(row.amount_vat || currency(0)),
                    currency(0),
                  ),
                )}
              </td>
              <td>
                {renderCurrency(
                  rows.reduce(
                    (acc, row) =>
                      acc.add(
                        row.amount_total.subtract(
                          row.amount_vat || currency(0),
                        ),
                      ),
                    currency(0),
                  ),
                )}
              </td>
              <td></td>
            </tr>
          );

        return (
          <div>
            {vatSelectedExplanation}
            {downloadLinks}
            <table id={id}>
              {headerRow}
              {renderedRows}
              {renderedTotal}
            </table>
          </div>
        );
      })}
    </main>
  );
}

export function runQueryOnFormChange<T, Body, Returns>(
  source: Source<Parsing<T>>,
  spec: APISpec<void, Body, Returns>,
  mp: (t: T) => Body,
): Source<
  | { tag: "initial" }
  | { tag: "loading" }
  | { tag: "loaded"; item: Returns; query: T }
  | { tag: "error"; error: string }
> {
  const res: Source<
    | { tag: "initial" }
    | { tag: "loading" }
    | { tag: "loaded"; item: Returns; query: T }
    | { tag: "error"; error: string }
  > = new Source({ tag: "initial" });

  const runQuery = debounce(async (t: T) => {
    res.set({ tag: "loading" });
    rpc(spec, undefined, mp(t))
      .then((rows) => res.set({ tag: "loaded", item: rows, query: t }))
      .catch((err) =>
        res.set({ tag: "error", error: tryExtractErrorMessage(err) }),
      );
  }, 150);

  const curr = source.get();
  if (curr.tag === "parsed") {
    void runQuery(curr.parsed);
  }

  scheduleForCleanup(
    source.observe((form) => {
      if (form.tag === "parsed") {
        void runQuery(form.parsed);
      }
    }),
  );
  return res;
}

export function renderReportResultWithSorting<Rows, PossibleSorting, T>(
  s: Source<
    | { tag: "initial" }
    | { tag: "loading" }
    | { tag: "loaded"; item: Rows; query: T }
    | { tag: "error"; error: string }
  >,
  sortingS: Source<{ sorting: PossibleSorting; direction: "asc" | "desc" }>,
  render: (
    rows: Rows,
    query: T,
    sorting: { sorting: PossibleSorting; direction: "asc" | "desc" },
  ) => HTMLElement | HTMLElement[],
): HTMLElement | HTMLElement[] {
  return dynVariadic([s, sortingS], function ([vals, sorting]) {
    if (vals.tag === "initial") {
      return <span></span>;
    } else if (vals.tag === "loading") {
      return (
        <div>
          <span className={style.loading}></span>
        </div>
      );
    } else if (vals.tag === "error") {
      return <div style="color: red;">{vals.error}</div>;
    } else if (vals.tag === "loaded") {
      return render(vals.item, vals.query, sorting);
    } else {
      return checkAllCasesHandled(vals);
    }
  });
}

export function renderReportResult<Rows, T>(
  s: Source<
    | { tag: "initial" }
    | { tag: "loading" }
    | { tag: "loaded"; item: Rows; query: T }
    | { tag: "error"; error: string }
  >,
  render: (rows: Rows, query: T) => HTMLElement | HTMLElement[],
): HTMLElement | HTMLElement[] {
  return dyn(s, function (vals) {
    if (vals.tag === "initial") {
      return <span></span>;
    } else if (vals.tag === "loading") {
      return (
        <div>
          <span className={style.loading}></span>
        </div>
      );
    } else if (vals.tag === "error") {
      return <div style="color: red;">{vals.error}</div>;
    } else if (vals.tag === "loaded") {
      return render(vals.item, vals.query);
    } else {
      return checkAllCasesHandled(vals);
    }
  });
}

export function invoiceSubjectFilterField(opts: {
  hasPeriodFees: boolean;
  internalTeachers: {
    firstname: string;
    lastname: string;
    internalteacherid: internalteacherid;
  }[];
}): Field<{ subject: invoiceSubjectFilter }> {
  return new Form({})
    .addField("subject_choice", [], async () =>
      selectBox({
        values: opts.hasPeriodFees
          ? [
              "all" as const,
              "enrollment" as const,
              "periodfee" as const,
              "student_charge" as const,
            ]
          : ["all" as const, "enrollment" as const, "student_charge" as const],
        initial: "all" as const,
        show: (s) =>
          s === "all"
            ? "Allemaal"
            : s === "enrollment"
            ? "Lesgeld"
            : s === "periodfee"
            ? "Jaargeld"
            : s === "student_charge"
            ? "Losse factuur"
            : checkAllCasesHandled(s),
        label: "Type",
      }),
    )
    .addField("subject", ["subject_choice"], async ({ subject_choice }) => {
      if (subject_choice === "all") {
        return constantField({ tag: "all" as const });
      } else if (subject_choice === "periodfee") {
        return constantField({ tag: "periodfee" as const });
      } else if (subject_choice === "student_charge") {
        return constantField({ tag: "student_charge" as const });
      } else if (subject_choice === "enrollment") {
        return new Form({})
          .addField("tag", [], () => constantField("enrollment" as const))
          .addField("teacher_", [], async () => {
            const values = [
              {
                label: "Allemaal",
                values: opts.internalTeachers,
              },
              ...opts.internalTeachers.map((t) => ({
                label: renderFirstNameLastName(t),
                values: [t],
              })),
            ];
            return selectBox({
              values: values,
              initial: values[0],
              show: (t) => t.label,
              label: "Docenten",
            });
          })
          .addField("teachers", ["teacher_"], async ({ teacher_ }) =>
            constantField(teacher_.values),
          )
          .build();
      } else {
        return checkAllCasesHandled(subject_choice);
      }
    })
    .build({
      render: (fields) => (
        <div>
          {fields.subject_choice()}
          {fields.subject()}
        </div>
      ),
    });
}
